/**
 * Manager hierarchy.
 *
 * `managedBy` says who manages a person directly. That is the decision an admin
 * makes, and it stays the only source of truth. `managerChain` is DERIVED from
 * it — every manager above someone, nearest first — and exists for one reason:
 *
 *   Firestore rules have no loops. You cannot walk up a chain until you find
 *   the requester, so "can Linda see Marko's team?" is not a question the rules
 *   can ask by traversal. Denormalising the chain onto each person turns it
 *   into one `in` test, which a LIST query can also be constrained by
 *   (`array-contains`). That last part is the whole point: a rule that only
 *   worked on single documents would let the console fetch the roster and
 *   filter afterwards, which is exactly the hole the manager scoping closed.
 *
 * Being derived, it can go stale. Anything that changes `managedBy` has to
 * rewrite the chain for that person AND everybody beneath them — see
 * `chainsAfterChange`, which is what the admin page saves.
 */

/**
 * How far up a chain we will look.
 *
 * Not a guess at how tall Finder is: it is a backstop so a cycle in the data
 * cannot produce an unbounded walk. Cycles are prevented on write (`wouldCycle`)
 * but data written before this existed has no such guarantee.
 */
export const MAX_CHAIN = 12

/**
 * uid → the uid of their direct manager, for the people who have one.
 *
 * Only people with a uid can appear: a chain is made of manager identities, and
 * somebody who has never signed in has no identity to name. They can still
 * HAVE a manager — that waits in their assignment — they just cannot be one.
 */
export function managerMap(people = []) {
  const map = new Map()
  for (const person of people) {
    if (person?.uid && person.managedBy) map.set(person.uid, person.managedBy)
  }
  return map
}

/**
 * Everyone above this person, nearest first.
 *
 * Stops on a repeat rather than looping forever, and never returns the person
 * themselves — a cycle would otherwise put them in their own chain, which reads
 * as "may see their own team" and is a permission, not a curiosity.
 */
export function chainFor(uid, managerOf) {
  const chain = []
  if (!uid) return chain
  const seen = new Set([uid])
  let current = managerOf.get(uid)
  while (current && !seen.has(current) && chain.length < MAX_CHAIN) {
    chain.push(current)
    seen.add(current)
    current = managerOf.get(current)
  }
  return chain
}

/**
 * Would naming `managerUid` as `personUid`'s manager create a loop?
 *
 * Checked before saving, because a cycle is silent: everyone in it keeps a
 * plausible-looking chain, and the people below it lose the managers above.
 */
export function wouldCycle(personUid, managerUid, managerOf) {
  if (!managerUid || !personUid) return false
  if (personUid === managerUid) return true
  return chainFor(managerUid, managerOf).includes(personUid)
}

/**
 * Everyone below this person, at any depth.
 *
 * Walks downward from the direct reports rather than testing every person's
 * chain, so it does not depend on the chains being correct — which is the point,
 * since it is used to fix them.
 */
export function descendantsOf(uid, people = []) {
  const childrenOf = new Map()
  for (const person of people) {
    if (!person?.managedBy) continue
    const list = childrenOf.get(person.managedBy) || []
    list.push(person)
    childrenOf.set(person.managedBy, list)
  }

  const out = []
  const seen = new Set([uid])
  const queue = [...(childrenOf.get(uid) || [])]
  while (queue.length) {
    const person = queue.shift()
    if (!person.uid) { out.push(person); continue } // a leaf: cannot have reports
    if (seen.has(person.uid)) continue              // cycle guard
    seen.add(person.uid)
    out.push(person)
    queue.push(...(childrenOf.get(person.uid) || []))
  }
  return out
}

/**
 * Every chain that has to be written when one person's manager changes.
 *
 * Returns rows rather than a map so the caller knows WHICH records to write —
 * a person appears here whether or not they have signed in, because a pending
 * assignment needs the chain too or their manager's manager cannot see them
 * until somebody re-saves.
 *
 * The change is applied to a copy of the roster first, so descendant chains are
 * computed against the new state rather than the old one.
 */
export class CycleError extends Error {}

export function chainsAfterChange(people = [], personUid, newManagerUid, personEmail = null) {
  // I5. The dropdown also hides these options, but a filtered <option> list is
  // an affordance: `assign()`, a script, a console edit or legacy data can all
  // produce a loop, and a loop is silent — everyone in it keeps a plausible
  // chain while the people below lose the managers above. Refusing here means
  // no caller can create one.
  if (personUid && wouldCycle(personUid, newManagerUid, managerMap(people))) {
    throw new CycleError(`${newManagerUid} already reports to ${personUid}`)
  }

  const identify = (p) =>
    (personUid && p.uid === personUid) || (!personUid && personEmail && p.email === personEmail)

  const next = people.map((p) => (identify(p) ? { ...p, managedBy: newManagerUid || null } : p))
  const managerOf = managerMap(next)

  const subject = next.find(identify)
  const rows = subject ? [subject] : []
  // Only somebody with a uid can have reports, so an unsigned-in person's
  // change affects nobody below them.
  if (personUid) rows.push(...descendantsOf(personUid, next))

  return rows.map((person) => ({
    email: person.email,
    uid: person.uid || null,
    signedIn: !!person.uid,
    assigned: !!person.assigned,
    managedBy: person.managedBy || null,
    managerChain: person.uid
      ? chainFor(person.uid, managerOf)
      // No uid, so no key in managerOf: their chain starts at their manager.
      : (person.managedBy ? [person.managedBy, ...chainFor(person.managedBy, managerOf)] : []),
  }))
}


/**
 * The chain this person SHOULD have, worked out from `managedBy` alone.
 *
 * I4: every writer uses this rather than carrying a chain over from React state
 * or from the value already stored. A chain that round-trips through the client
 * can bring a stale permission back to life — a department edit re-committing
 * an old chain, adopted onto the account at the next sign-in, silently
 * restoring a manager who had been removed.
 */
export function expectedChain(person, managerOf) {
  if (!person) return []
  if (person.uid) return chainFor(person.uid, managerOf)
  // No uid means no entry in the map, so start from the manager they name.
  return person.managedBy
    ? [person.managedBy, ...chainFor(person.managedBy, managerOf)]
    : []
}

/**
 * Everyone whose stored chain disagrees with the hierarchy, and what it should
 * be. This is the backfill AND the drift report.
 *
 * Compares `accountManagerChain` — the copy on the USER document, which is what
 * the rules read — rather than a merged view. The first attempt compared a
 * merged roster row where the assignment won, i.e. the assignment against
 * itself, so it reported nothing and the repair skipped the broken rows.
 */
export function chainRepairs(people = []) {
  const managerOf = managerMap(people)
  const out = []
  for (const person of people) {
    const expected = expectedChain(person, managerOf)
    const onAccount = Array.isArray(person.accountManagerChain) ? person.accountManagerChain : null
    const onAssignment = Array.isArray(person.assignedManagerChain) ? person.assignedManagerChain : null
    const differs = (stored) => stored === null
      ? expected.length > 0
      : stored.length !== expected.length || stored.some((uid, i) => uid !== expected[i])

    const accountWrong = person.uid ? differs(onAccount) : false
    const assignmentWrong = person.assigned ? differs(onAssignment) : false
    if (accountWrong || assignmentWrong) {
      out.push({
        email: person.email,
        uid: person.uid || null,
        assigned: !!person.assigned,
        managedBy: person.managedBy || null,
        managerChain: expected,
        accountWrong,
        assignmentWrong,
      })
    }
  }
  return out
}
